import {
  INSTRUMENT_BATCH_TYPE_LABELS,
  INSTRUMENT_DIRECTION_LABELS,
  INSTRUMENT_KIND_LABELS,
  INSTRUMENT_STATUS_LABELS,
  type InstrumentDetail,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  FormField,
  MoneyText,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useQueries, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ImagePlus } from "lucide-react";
import { type ReactElement, type ReactNode, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../features/counterparties/shared.js";
import { applicableBatchTypes } from "../../../features/instruments/instrument-values.js";
import { useInstrument } from "../../../features/instruments/queries.js";
import {
  BatchActionsMenu,
  InstrumentPage,
  InstrumentStatusBadge,
  RiskyDrawerBadge,
  useInstrumentCrumb,
  useInstrumentPermissions,
  useRefreshInstruments,
} from "../../../features/instruments/shared.js";
import { formatIban } from "../../../features/treasury/treasury-values.js";
import { uploadFile } from "../../../features/station-import.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../lib/format.js";
import { queryKeys } from "../../../lib/query.js";
import { usePermissions } from "../../../lib/session.js";

export const Route = createFileRoute("/_app/cek-senet/$id")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: InstrumentDetailPage,
});

function InstrumentDetailPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const crumb = useInstrumentCrumb("/cek-senet");
  const detail = useInstrument(id);
  const instrument = detail.data;
  const title = instrument ? instrument.portfolioNo : t("instruments.detail.title");
  return (
    <InstrumentPage
      title={title}
      {...(instrument
        ? {
            description: `${INSTRUMENT_KIND_LABELS[instrument.kind]} · ${INSTRUMENT_DIRECTION_LABELS[instrument.direction]}`,
          }
        : {})}
      breadcrumb={[crumb, { label: title }]}
      actions={
        instrument ? (
          <BatchActionsMenu
            label={t("instruments.detail.actOn")}
            types={applicableBatchTypes([instrument])}
            instrumentIds={[instrument.id]}
          />
        ) : null
      }
    >
      {() =>
        detail.error ? (
          <Alert tone="danger">{errorMessage(detail.error)}</Alert>
        ) : !instrument ? (
          <SkeletonRows rows={8} />
        ) : (
          <InstrumentBody instrument={instrument} />
        )
      }
    </InstrumentPage>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactElement {
  const headingId = useId();
  return (
    <Card className="grid gap-3 p-4 sm:p-5" aria-labelledby={headingId}>
      <h2 id={headingId} className="text-base font-semibold">
        {title}
      </h2>
      {children}
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

function InstrumentBody({ instrument }: { instrument: InstrumentDetail }): ReactElement {
  const { t } = useTranslation();
  const isCheque = instrument.kind === "CHEQUE";
  return (
    <div className="grid gap-4">
      {instrument.isVoid ? (
        <Alert tone="warning" title={t("instruments.detail.voidTitle")}>
          {instrument.voidReason ?? ""}
        </Alert>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="grid gap-4 lg:col-span-2">
          <Section title={t("instruments.detail.identity")}>
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label={t("instruments.fields.status")}>
                <InstrumentStatusBadge status={instrument.status} />
              </Field>
              <Field label={t("instruments.fields.location")}>{instrument.locationLabel}</Field>
              <Field label={t("instruments.fields.counterparty")}>
                <Link
                  to="/cari/$id"
                  params={{ id: instrument.counterpartyId }}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {instrument.counterpartyName}
                </Link>
              </Field>
              {isCheque ? (
                <>
                  <Field label={t("instruments.fields.serialNo")}>
                    <span className="font-mono">{instrument.serialNo ?? "—"}</span>
                  </Field>
                  <Field label={t("instruments.fields.bank")}>{instrument.bankName ?? "—"}</Field>
                  <Field label={t("instruments.fields.branch")}>
                    {[instrument.branchName, instrument.branchCode].filter(Boolean).join(" · ") || "—"}
                  </Field>
                  <Field label={t("instruments.fields.accountNo")}>
                    <span className="font-mono">{instrument.accountNo ?? "—"}</span>
                  </Field>
                  {instrument.iban ? (
                    <Field label={t("instruments.fields.iban")}>
                      <span className="font-mono">{formatIban(instrument.iban)}</span>
                    </Field>
                  ) : null}
                </>
              ) : null}
              <Field label={t("instruments.fields.drawer")}>
                <span className="flex flex-wrap items-center gap-1.5">
                  {instrument.drawerName}
                  {instrument.drawerTaxId ? (
                    <span className="font-mono text-xs text-muted-foreground">{instrument.drawerTaxId}</span>
                  ) : null}
                  {instrument.riskyDrawer ? <RiskyDrawerBadge /> : null}
                </span>
              </Field>
              {instrument.payeeName ? (
                <Field label={t("instruments.fields.payee")}>{instrument.payeeName}</Field>
              ) : null}
              {instrument.ownBankAccountName ? (
                <Field label={t("instruments.fields.ownBankAccount")}>{instrument.ownBankAccountName}</Field>
              ) : null}
              <Field label={t("instruments.fields.issueDate")}>
                <span className="tabular">
                  {formatDate(instrument.issueDate)}
                  {instrument.issuePlace ? ` · ${instrument.issuePlace}` : ""}
                </span>
              </Field>
              <Field label={t("instruments.fields.dueDate")}>
                <span className="tabular">{formatDate(instrument.dueDate)}</span>
              </Field>
            </dl>
            <dl className="grid gap-x-6 gap-y-3 border-t pt-3 sm:grid-cols-2 lg:grid-cols-4">
              <Field label={t("instruments.fields.amount")}>
                <MoneyText value={instrument.amount} />
              </Field>
              <Field label={t("instruments.fields.extraAmount")}>
                <MoneyText value={instrument.extraAmount} />
              </Field>
              <Field label={t("instruments.fields.collectedAmount")}>
                <MoneyText value={instrument.collectedAmount} />
              </Field>
              <Field label={t("instruments.fields.remaining")}>
                <MoneyText value={instrument.remainingAmount} className="text-base font-semibold" />
              </Field>
            </dl>
          </Section>

          <HistorySection instrument={instrument} />
          {instrument.endorsements.length > 0 ? <EndorsementSection instrument={instrument} /> : null}
        </div>

        <div className="grid content-start gap-4">
          <ImagesSection instrument={instrument} />
          <NotesSection instrument={instrument} />
          <EntriesSection instrument={instrument} />
        </div>
      </div>
    </div>
  );
}

function HistorySection({ instrument }: { instrument: InstrumentDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <Section title={t("instruments.detail.history")}>
      <SimpleTable
        caption={t("instruments.detail.history")}
        head={
          <tr>
            <th scope="col" className={thClass}>
              {t("instruments.fields.date")}
            </th>
            <th scope="col" className={thClass}>
              {t("instruments.fields.batch")}
            </th>
            <th scope="col" className={thClass}>
              {t("instruments.detail.transition")}
            </th>
            <th scope="col" className={thRightClass}>
              {t("instruments.fields.amount")}
            </th>
            <th scope="col" className={thClass}>
              {t("instruments.detail.changedBy")}
            </th>
          </tr>
        }
      >
        {instrument.history.map((entry) => (
          <tr key={entry.id} className={entry.isRollback ? "border-t bg-muted/40" : "border-t"}>
            <td className={`${tdClass} tabular whitespace-nowrap`}>{formatDateTime(entry.changedAt)}</td>
            <td className={tdClass}>
              <span className="grid gap-0.5">
                <Link
                  to="/cek-senet/bordrolar/$id"
                  params={{ id: entry.batchId }}
                  className="font-mono text-xs font-semibold text-primary underline-offset-4 hover:underline"
                >
                  {entry.batchNo}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {INSTRUMENT_BATCH_TYPE_LABELS[entry.batchType]}
                </span>
              </span>
            </td>
            <td className={tdClass}>
              <span className="flex flex-wrap items-center gap-1.5">
                {entry.fromStatus ? INSTRUMENT_STATUS_LABELS[entry.fromStatus] : t("instruments.detail.new")}
                <span aria-hidden="true">→</span>
                <span className="sr-only">{t("instruments.detail.to")}</span>
                {INSTRUMENT_STATUS_LABELS[entry.toStatus]}
                {entry.isRollback ? (
                  <StatusBadge tone="warning">{t("instruments.detail.rollback")}</StatusBadge>
                ) : null}
              </span>
              {entry.note ? <span className="block text-xs text-muted-foreground">{entry.note}</span> : null}
            </td>
            <td className={tdRightClass}>
              <MoneyText value={entry.amount} />
            </td>
            <td className={tdClass}>{entry.changedBy ?? t("instruments.detail.system")}</td>
          </tr>
        ))}
      </SimpleTable>
    </Section>
  );
}

function EndorsementSection({ instrument }: { instrument: InstrumentDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <Section title={t("instruments.detail.endorsements")}>
      <SimpleTable
        caption={t("instruments.detail.endorsements")}
        head={
          <tr>
            <th scope="col" className={thClass}>
              {t("instruments.detail.endorsedOn")}
            </th>
            <th scope="col" className={thClass}>
              {t("instruments.detail.from")}
            </th>
            <th scope="col" className={thClass}>
              {t("instruments.detail.toParty")}
            </th>
            <th scope="col" className={thClass}>
              {t("instruments.fields.status")}
            </th>
          </tr>
        }
      >
        {instrument.endorsements.map((endorsement) => (
          <tr key={endorsement.id} className="border-t">
            <td className={`${tdClass} tabular`}>{formatDate(endorsement.endorsedOn)}</td>
            <td className={tdClass}>{endorsement.fromCounterpartyName ?? "—"}</td>
            <td className={tdClass}>{endorsement.toCounterpartyName}</td>
            <td className={tdClass}>
              {t(`instruments.endorsementStatus.${endorsement.status}`)}
              {endorsement.closedOn ? ` · ${formatDate(endorsement.closedOn)}` : ""}
            </td>
          </tr>
        ))}
      </SimpleTable>
    </Section>
  );
}

/** Ön / arka yüz görseli: imzalı adrese yüklenir, onaylanır ve çeke bağlanır; görüntüleme kısa ömürlü bağlantıyla. */
function ImagesSection({ instrument }: { instrument: InstrumentDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <Section title={t("instruments.detail.images")}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
        <ImageSlot instrument={instrument} side="FRONT" fileId={instrument.frontFileId} />
        <ImageSlot instrument={instrument} side="BACK" fileId={instrument.backFileId} />
      </div>
    </Section>
  );
}

function ImageSlot({
  instrument,
  side,
  fileId,
}: {
  instrument: InstrumentDetail;
  side: "FRONT" | "BACK";
  fileId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInstruments();
  const canChange = useInstrumentPermissions().canChange;
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [busy, setBusy] = useState(false);
  const label = side === "FRONT" ? t("instruments.detail.front") : t("instruments.detail.back");
  const link = useQuery({
    queryKey: ["file-link", fileId],
    enabled: fileId !== null,
    staleTime: 240_000,
    queryFn: async () =>
      (await api.GET("/files/{id}/download-link", { params: { path: { id: fileId! } } })).data!,
  });

  async function upload(file: File): Promise<void> {
    setBusy(true);
    try {
      const uploaded = await uploadFile(file, "INSTRUMENT_IMAGE");
      await api.PUT("/instruments/{id}/images", {
        params: { path: { id: instrument.id } },
        body: { side, fileId: uploaded },
      });
      await refresh(instrument.companyId);
      toast({ tone: "success", title: t("instruments.detail.imageSaved") });
    } catch (error) {
      toast({ tone: "danger", title: errorMessage(error) });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <figure className="grid gap-2">
      <figcaption className="text-sm font-medium">{label}</figcaption>
      {fileId && link.data ? (
        <a href={link.data.url} target="_blank" rel="noreferrer" className="block">
          <img
            src={link.data.url}
            alt={t("instruments.detail.imageAlt", { side: label, no: instrument.portfolioNo })}
            className="max-h-48 w-full rounded-md border object-contain"
          />
        </a>
      ) : (
        <div className="flex h-24 items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
          {fileId ? t("common.loading") : t("instruments.detail.noImage")}
        </div>
      )}
      {canChange ? (
        <>
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="sr-only"
            aria-label={t("instruments.detail.uploadImage", { side: label })}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <Button variant="outline" size="sm" loading={busy} onClick={() => inputRef.current?.click()}>
            <ImagePlus aria-hidden="true" />
            {fileId ? t("instruments.detail.replaceImage") : t("instruments.detail.addImage")}
          </Button>
        </>
      ) : null}
    </figure>
  );
}

function NotesSection({ instrument }: { instrument: InstrumentDetail }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInstruments();
  const canChange = useInstrumentPermissions().canChange;
  const [notes, setNotes] = useState(instrument.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = notes.trim() !== (instrument.notes ?? "");
  return (
    <Section title={t("instruments.fields.notes")}>
      <form
        className="grid gap-2"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          api
            .PATCH("/instruments/{id}", {
              params: { path: { id: instrument.id } },
              body: { notes: notes.trim() || null, version: instrument.version },
            })
            .then(async () => {
              await refresh(instrument.companyId);
              toast({ tone: "success", title: t("common.saved") });
            })
            .catch((caught: unknown) => setError(errorMessage(caught)))
            .finally(() => setBusy(false));
        }}
      >
        <FormField label={t("instruments.fields.notes")} error={error ?? undefined}>
          {(control) => (
            <Textarea
              {...control}
              rows={3}
              maxLength={1000}
              value={notes}
              disabled={!canChange}
              onChange={(event) => setNotes(event.target.value)}
            />
          )}
        </FormField>
        {canChange ? (
          <div className="flex justify-end">
            <Button type="submit" size="sm" loading={busy} disabled={!changed}>
              {t("common.save")}
            </Button>
          </div>
        ) : null}
      </form>
    </Section>
  );
}

/** Çeke ait fişler (çek boyutlu satırı olanlar): muhasebe fiş sayfasına bağlantı; fiş numarası izin varsa çözülür. */
function EntriesSection({ instrument }: { instrument: InstrumentDetail }): ReactElement {
  const { t } = useTranslation();
  const canRead = usePermissions().has("accounting.entry.read");
  const entries = useQueries({
    queries: instrument.journalEntryIds.map((id) => ({
      queryKey: queryKeys.accountingEntry(id),
      enabled: canRead,
      staleTime: 300_000,
      queryFn: async () =>
        (await api.GET("/accounting/journal-entries/{id}", { params: { path: { id } } })).data!,
    })),
  });
  return (
    <Section title={t("instruments.detail.entries")}>
      {instrument.journalEntryIds.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("instruments.detail.noEntries")}</p>
      ) : (
        <ul className="grid gap-1 text-sm">
          {instrument.journalEntryIds.map((id, index) => {
            const entry = entries[index]?.data;
            return (
              <li key={id}>
                <Link
                  to="/muhasebe/fisler/$id"
                  params={{ id }}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {entry
                    ? `${entry.entryNo ?? "—"} · ${formatDate(entry.documentDate)} · ${entry.description}`
                    : t("instruments.detail.entryN", { n: index + 1 })}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
